import { Card } from "~/features/configurator/Linear/components/Card";
import { CircularProgress, Stack, styled, Typography } from "~/common/mui";
import { IMG_NO_PRODUCT_BUNDLE } from "~/utils/url/staticResources";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";
import { Product } from "~/domains/product/Product";
import Checkbox from "~/common/components/Checkbox";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { Price } from "~/domains/pricing/components/Price/v3/Price";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import type { ChangeEvent } from "react";
import { getPackagingInformation } from "~/domains/product/utils/productDescriptorUtils";
import type { NetPriceData } from "~/domains/pricing/types/PriceData";
import { useTranslation } from "react-i18next";
import type { NetPriceType } from "~/utils/v2/priceSelectHelper";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { C_ON_SURFACE_VARIANT } from "~/common/components/icon/Colors";
import { appendPurchaseParams } from "~/features/productDetails/components/ProductSummary/PdpSummaryHelper";
import { usePurchasePlace } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import type { PurchaseType } from "~/app/analytics/ga/recommendations/types";

interface Props {
  className?: string;
  loading?: boolean;
  product?: ProductDescriptor;
  main?: boolean;
  selected?: boolean;
  onSelect?: ((event: ChangeEvent<HTMLInputElement>, checked: boolean) => void) | undefined;
  quantity?: number;
  onQuantityChange?: (newValue: number) => void;
  packageCode?: string;
  price?: NetPriceData | null;
  priceType?: NetPriceType;
}

export const FrequentlyBoughtTogetherItem = ({
  loading,
  product,
  className,
  selected,
  onSelect,
  main,
  quantity,
  onQuantityChange,
  packageCode,
  price,
  priceType,
}: Props) => {
  const reco_place = usePurchasePlace();
  const reco_type: PurchaseType = "fbt" as const;
  const { t } = useTranslation();
  const showSaleTag = useSalesHelper(product).isPackageOnSale(packageCode);

  if (loading) {
    return (
      <StyledCard className={className}>
        <CenteredStacker gap={1}>
          <CircularProgress color={"primary"} size={32} />
          <Typography color={"primary"} variant={"bodyMedium"}>
            {t("feature.frequentlyBoughtTogether.loading")}
          </Typography>
        </CenteredStacker>
      </StyledCard>
    );
  }

  if (!product) {
    return (
      <StyledCard className={className}>
        <CenteredStacker gap={2}>
          <Typography variant={"bodySmall"}>{t("feature.frequentlyBoughtTogether.noThird")}</Typography>
          <img src={IMG_NO_PRODUCT_BUNDLE} height={64} />
        </CenteredStacker>
      </StyledCard>
    );
  }

  let priceElement;
  if (!userHasPermission(Permission.PRICE)) {
    priceElement = <div />;
  } else {
    if (product.isPriceLoading) {
      priceElement = (
        <CenteredStacker gap={1}>
          <CircularProgress color={"primary"} size={16} />
        </CenteredStacker>
      );
    } else if (price) {
      priceElement = (
        <Price
          as="b"
          price={price}
          className={priceType === "salesPrice" ? "f-green" : undefined}
          zeroText={t("product.priceOnRequest")}
        />
      );
    }
  }

  let packageInfo;
  let step;
  if (!userHasPermission(Permission.AVAILABILITY)) {
    packageInfo = null;
    step = 1;
  } else if (packageCode) {
    const packagingInfo = getPackagingInformation(product, packageCode);
    if (packagingInfo) {
      packageInfo = (
        <>
          <Typography variant={"labelSmall"} sx={{ color: C_ON_SURFACE_VARIANT }}>
            {t("addToCartPopup.packSize", { amount: packagingInfo.packSize, code: packagingInfo.packCode })}
          </Typography>
        </>
      );
      step = packagingInfo.packSize;
    }
  }
  // Fallback
  if (!step) {
    step = 1;
  }

  const pdpUrl = appendPurchaseParams(product.base.url, reco_place, reco_type);

  return (
    <ProductDescriptorContext value={product}>
      <StyledCard className={className} active={selected}>
        <Layout>
          <BaseInfo>
            {showSaleTag && <PositionedSalesTag size={32} />}
            <Product.PrimaryImage clickable={!main} linkProps={{ target: "_blank", href: pdpUrl }} />
            <Stack gap={0.5}>
              <Typography variant={"bodyLarge"} sx={{ wordBreak: "break-word" }}>
                {main && <span>{t("feature.frequentlyBoughtTogether.thisItem")} </span>}
                <Designation component={"b"} clickable={!main} linkProps={{ target: "_blank", href: pdpUrl }} />
              </Typography>
              <Product.ShortDescription clickable={false} />
            </Stack>
            <Checkbox checked={selected} onChange={onSelect} />
          </BaseInfo>
          <DetailInfo>
            {priceElement}
            <QuantityInput
              variant={"compact"}
              value={quantity}
              onValueChange={onQuantityChange}
              step={step}
              min={step}
            />
            {packageInfo}
          </DetailInfo>
        </Layout>
      </StyledCard>
    </ProductDescriptorContext>
  );
};

const PositionedSalesTag = styled(SaleTag)({
  position: "absolute",
  top: 0,
  left: 0,
});

const StyledCard = styled(Card)({
  width: "100%",
  height: "100%",
  padding: 16,
  background: "white",
});
const Layout = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 8,
  width: "100%",
  height: "100%",

  [theme.breakpoints.between("tablet", "desktop-s")]: {
    display: "grid",
    gridTemplateColumns: "64px 1fr 25% 124px auto",
    gridAutoFlow: "dense",
  },
}));

const BaseInfo = styled("div")(({ theme }) => ({
  width: "100%",
  display: "grid",
  gridTemplateColumns: "64px 1fr auto",
  gap: 8,
  alignContent: "flex-start",

  [theme.breakpoints.between("tablet", "desktop-s")]: {
    display: "contents",

    "*:nth-child(1)": {
      gridRow: "1 / span 2",
    },

    "*:nth-child(2)": {
      gridRow: "1 / span 2",
    },

    "*:nth-child(3)": {
      gridColumn: 5,
    },
  },
}));

const DetailInfo = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 180px",
  gap: "8px 4px",
  alignContent: "flex-start",
  alignItems: "center",
  marginTop: "auto",

  "*:nth-child(3)": {
    gridColumn: 2,
  },

  [theme.breakpoints.between("tablet", "desktop-s")]: {
    display: "contents",
    gap: 16,

    "*:nth-child(1)": {
      gridColumn: 3,
    },

    "*:nth-child(3)": {
      gridColumn: 4,
    },
  },
}));

const CenteredStacker = styled(Stack)({
  placeContent: "center",
  placeItems: "center",
  textAlign: "center",
  height: "100%",
  width: "100%",
});

const Designation = styled(Product.FullDesignation)({
  display: "inline-flex",
});
